import { FC, useCallback } from 'react';
import API from 'core/Api';
import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import { FaCheck, FaUserCheck } from 'react-icons/fa';
import { createErrorFromResponse } from 'core/Error';
import { useModal } from 'store/modal';
import Spinner from 'components/Spinner';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface IProps {
  id: string;
  buttonText: string;
  disabled: boolean;
  className?: string;
  invalidate: () => void;
  onClick?: () => void;
}

const ReviewDoc: FC<IProps> = ({ id, buttonText, disabled, invalidate, className, onClick = () => {} }: IProps) => {
  const { showModal } = useModal();
  const { t } = useTranslation();

  const { isLoading, data, refetch } = useQuery<{ data: { success: boolean } }>({
    queryFn: () => API.post(`leaves/${id}/review`),
    queryKey: [`review-leave-sick-${id}`],
    enabled: false,
    cacheTime: 0,
    onSuccess: () => {
      invalidate();
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (err: any) => {
      invalidate();
      showModal({
        type: 'error',
        content: createErrorFromResponse(err).message,
      });
    },
  });

  const onConfirm = useCallback(() => {
    setTimeout(() => {
      refetch();
      onClick();
    });
  }, [onClick, refetch]);

  const containerClass = className ?? 'ml-2';

  return (
    <div className={`d-inline-block ${containerClass}`}>
      <TooltipWrapper
        id="tooltip-review"
        overlay={data?.data?.success === true ? t('common.success') : buttonText}
      >
        <Button
          ariaLabel="Review Leave Button"
          type="button"
          btnType="secondary"
          size="sm"
          disabled={data?.data.success ?? disabled}
          onClick={e => {
            showModal({
              type: 'confirm',
              content: <ConfirmText>{t('documents.table.modalReviewPrompt')}</ConfirmText>,
              confirmLabel: t('common.yes'),
              cancelLabel: t('common.no'),
              onConfirm,
            });
            e.stopPropagation();
          }}
        >
          {!isLoading && data?.data?.success !== true && <FaUserCheck size={14} />}
          {isLoading && <Spinner />}
          {data?.data?.success === true && <FaCheck size={14} />}
        </Button>
      </TooltipWrapper>
    </div>
  );
};

export default ReviewDoc;

const ConfirmText = styled.p`
  font-size: 1.125rem;
`;
